import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash2, UserCog, Building2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { initials } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatApiErrorDetail as fmtErr } from "@/lib/api";

const ROLE_LABEL = { engineer: "Employee", client: "Client", manager: "Manager" };

function CreateUserDialog({ defaultRole, canAddManager, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(defaultRole);
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!name.trim() || !email.trim() || !password.trim()) { toast.error("All fields required"); return; }
    setLoading(true);
    try {
      await api.post("/users", { name, email, password, role, company: role === "client" ? company : "" });
      toast.success(`${ROLE_LABEL[role]} created`);
      onCreated?.(); onClose();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Could not create user");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-user-dialog">
        <DialogHeader><DialogTitle className="font-display">Add team member</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="engineer">Employee</SelectItem>
                <SelectItem value="client">Client</SelectItem>
                {canAddManager && <SelectItem value="manager">Manager</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Full name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="user-name-input" /></div>
          {role === "client" && (
            <div className="space-y-2"><Label>Company</Label><Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme Corporation" data-testid="user-company-input" /></div>
          )}
          <div className="space-y-2"><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="user-email-input" /></div>
          <div className="space-y-2"><Label>Temporary password</Label><Input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Share this with them" data-testid="user-password-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="user-create-submit">
            {loading ? "Creating…" : "Create account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserCard({ u, onDelete, onToggleHr }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-center gap-3" data-testid={`user-card-${u.id}`}>
      <Avatar className="w-11 h-11"><AvatarImage src={u.avatar} /><AvatarFallback className="bg-indigo-100 text-indigo-700 font-semibold">{initials(u.name)}</AvatarFallback></Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 truncate">{u.name}</p>
        <p className="text-xs text-slate-500 truncate">{u.email}</p>
        {u.is_owner && <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 mt-0.5" data-testid={`owner-badge-${u.id}`}>Owner</p>}
        {u.company && <p className="text-xs text-indigo-600 flex items-center gap-1 mt-0.5"><Building2 className="w-3 h-3" />{u.company}</p>}
      </div>
      <Badge variant="outline" className="h-5 px-1.5 text-[10px] uppercase tracking-wide border-slate-200 text-slate-500" data-testid={`role-badge-${u.id}`}>{ROLE_LABEL[u.role] || u.role}</Badge>
      {u.is_hr && !onToggleHr && <Badge variant="outline" className="h-5 px-1.5 text-[10px] uppercase tracking-wide border-violet-200 bg-violet-50 text-violet-700" data-testid={`hr-badge-${u.id}`}>HR</Badge>}
      {onToggleHr && (
        <label className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 cursor-pointer">
          HR <Switch checked={!!u.is_hr} onCheckedChange={(v) => onToggleHr(u, v)} data-testid={`hr-toggle-${u.id}`} />
        </label>
      )}
      {onDelete && (
        <button onClick={() => onDelete(u)} className="text-slate-300 hover:text-red-500" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></button>
      )}
    </div>
  );
}

export default function TeamPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(null);

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}? This cannot be undone.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  const toggleHr = user.is_owner ? async (u, v) => {
    try { await api.patch(`/users/${u.id}/hr`, { is_hr: v }); toast.success(v ? `${u.name} is now HR` : `HR access removed from ${u.name}`); load(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  } : null;

  const engineers = users.filter((u) => u.role === "engineer");
  const clients = users.filter((u) => u.role === "client");
  const managers = users.filter((u) => u.role === "manager" || u.role === "admin");

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1100px]" data-testid="team-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Team &amp; Clients</h1>
          <p className="text-sm text-slate-500 mt-1">Create employee and client accounts and manage access.</p>
        </div>
      </div>

      <Tabs defaultValue="engineers">
        <TabsList className="mb-4">
          <TabsTrigger value="engineers" data-testid="team-engineers-tab">Employees ({engineers.length})</TabsTrigger>
          <TabsTrigger value="clients" data-testid="team-clients-tab">Clients ({clients.length})</TabsTrigger>
          <TabsTrigger value="managers" data-testid="team-managers-tab">Managers ({managers.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="engineers">
          <div className="flex justify-end mb-3">
            <Button onClick={() => setCreating("engineer")} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-engineer-button"><Plus className="w-4 h-4 mr-1.5" />Add employee</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {engineers.map((u) => <UserCard key={u.id} u={u} onDelete={remove} onToggleHr={toggleHr} />)}
          </div>
        </TabsContent>

        <TabsContent value="clients">
          <div className="flex justify-end mb-3">
            <Button onClick={() => setCreating("client")} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-client-button"><Plus className="w-4 h-4 mr-1.5" />Add client</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {clients.map((u) => <UserCard key={u.id} u={u} onDelete={remove} />)}
            {clients.length === 0 && <p className="text-sm text-slate-400">No clients yet. Add one to give them a portal login.</p>}
          </div>
        </TabsContent>

        <TabsContent value="managers">
          {user.is_owner ? (
            <div className="flex justify-end mb-3">
              <Button onClick={() => setCreating("manager")} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-manager-button"><Plus className="w-4 h-4 mr-1.5" />Add manager</Button>
            </div>
          ) : (
            <p className="text-xs text-slate-500 mb-3" data-testid="managers-owner-note">Only the owner can add or remove managers.</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {managers.map((u) => <UserCard key={u.id} u={u} onDelete={user.is_owner && !u.is_owner ? remove : null} onToggleHr={toggleHr} />)}
          </div>
        </TabsContent>
      </Tabs>

      {creating && <CreateUserDialog defaultRole={creating} canAddManager={!!user.is_owner} onClose={() => setCreating(null)} onCreated={load} />}
    </div>
  );
}
